import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui';
import { useT } from '@/i18n/react';
import { markUnlocked, requestUnlock, UnlockError } from '@/lib/appLock';
import './appLock.css';

/**
 * A ZÁRKÉPERNYŐ — az app elé áll, amíg a tulajdonos fel nem oldja.
 *
 * ⚠️ MAGÁTÓL KÉRDEZ, EGYSZER. A rendszerpárbeszédet nem a felhasználónak kell
 * előhívnia egy gombbal: az app épp most indult, a szándéka egyértelmű. Ha
 * megszakítja vagy nem sikerül, ITT MARAD egy újrapróbálás gombbal — nem
 * nyitjuk ki, és nem is kezdjük el újra magunktól, mert az egy
 * megszakíthatatlan párbeszéd-hurok lenne.
 *
 * ⚠️ VAN KIÚT: „Kijelentkezés". Enélkül az, akinek elromlott vagy megszűnt a
 * biometriája, örökre egy megnyithatatlan képernyőt kapna, és az appot csak
 * újratelepítéssel tudná használni. A kijelentkezés a zárat is értelmetlenné
 * teszi, tehát a következő indulás már a belépőt hozza.
 */
export function AppLockGate({
  onUnlocked,
  onSignOut,
}: {
  onUnlocked: () => void;
  onSignOut: () => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  /* ⚠️ StrictMode-ban az effekt kétszer fut — enélkül két rendszerpárbeszéd
     nyílna egymásra, és a második azonnal hibára futna. */
  const started = useRef(false);

  const unlock = useCallback(() => {
    setBusy(true);
    setError('');
    requestUnlock(t.appLock.unlockPrompt)
      .then(() => {
        markUnlocked();
        onUnlocked();
      })
      .catch((cause: unknown) => {
        if (cause instanceof UnlockError && cause.reason === 'cancelled') {
          setError(t.appLock.errors.cancelled);
        } else {
          setError(cause instanceof Error ? cause.message : t.appLock.errors.failed);
        }
      })
      .finally(() => setBusy(false));
  }, [onUnlocked, t]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    unlock();
  }, [unlock]);

  return (
    <main className="applock" role="dialog" aria-modal="true" aria-label={t.appLock.dialogLabel}>
      <div className="applock__body">
        <LockIcon />
        <h1 className="applock__title">{t.appLock.title}</h1>
        <p className="applock__text">
          {t.appLock.description}
        </p>
        {error ? (
          <p className="applock__error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="applock__actions">
          <Button block loading={busy} onClick={unlock}>
            {t.appLock.unlock}
          </Button>
          <Button block variant="ghost" onClick={onSignOut} disabled={busy}>
            {t.appLock.signOut}
          </Button>
        </div>
      </div>
    </main>
  );
}

function LockIcon() {
  return (
    <svg
      className="applock__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="10.5" width="16" height="10.5" rx="2.5" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
      <path d="M12 14.5v2.5" />
    </svg>
  );
}
